/**
 * Shipping Insurance Auto-Add
 *
 * Keeps exactly one insurance line in the cart and selects its tier from the
 * discounted merchandise subtotal (insurance itself is always excluded):
 * - Under $500: Standard, $9.95
 * - $500–$999.99: Enhanced, $24.95
 * - $1,000+: Premium, $49.95
 *
 * Subscription cadence is owned by SubscriptionInsuranceCore and the UpCart
 * synchronizer. This module preserves a correctly tiered subscription line and
 * uses one-time insurance when it must rebuild a tier.
 */

class ShippingInsurance {
  constructor() {
    this.core = window.SubscriptionInsuranceCore;
    this.config = this.getConfig();
    this.isProcessing = false;
    this.autoAddSuppressed = Boolean(
      document.querySelector('[data-skip-shipping-insurance="true"]')
    );

    if (!this.config.enabled) return;
    if (!this.config.tiers.length) {
      console.warn('Shipping Insurance: No tier variant IDs configured');
      return;
    }
    this.init();
  }

  getConfig() {
    if (this.core?.getInsuranceConfig) {
      return this.core.getInsuranceConfig();
    }

    const settings = window.theme?.shopSettings || {};
    const tiers = Array.isArray(settings.shippingInsuranceTiers)
      ? settings.shippingInsuranceTiers
      : [{
          key: 'standard',
          variantId: settings.shippingInsuranceVariantId,
          minSubtotal: 0,
          maxSubtotal: null,
          supportsSubscription: true
        }];
    const legacyVariantIds = settings.shippingInsuranceLegacyVariantIds || [];
    const variantIds = tiers
      .map((tier) => String(tier.variantId || ''))
      .concat(legacyVariantIds.map(String))
      .filter(Boolean);

    return {
      enabled: Boolean(settings.shippingInsuranceEnabled),
      tiers,
      variantIds
    };
  }

  normalizeId(value) {
    if (this.core?.normalizeId) return this.core.normalizeId(value);
    const matches = String(value || '').match(/\d+/g);
    return matches?.length ? matches[matches.length - 1] : String(value || '');
  }

  isInsuranceVariant(value) {
    if (this.core?.isInsuranceVariantId) {
      return this.core.isInsuranceVariantId(value, this.config);
    }
    return this.config.variantIds.includes(this.normalizeId(value));
  }

  getMerchandiseSubtotal(items) {
    if (this.core?.getMerchandiseSubtotal) {
      return this.core.getMerchandiseSubtotal(items, this.config);
    }
    return (items || []).reduce((total, item) => {
      if (this.isInsuranceVariant(item.variant_id)) return total;
      return total + Number(item.final_line_price || item.line_price || 0);
    }, 0);
  }

  resolveTier(subtotal) {
    if (this.core?.resolveTierForSubtotal) {
      return this.core.resolveTierForSubtotal(subtotal, this.config);
    }
    return this.config.tiers.find((tier) => (
      subtotal >= Number(tier.minSubtotal || 0) &&
      (tier.maxSubtotal == null || subtotal < Number(tier.maxSubtotal))
    )) || this.config.tiers[this.config.tiers.length - 1];
  }

  init() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    document.addEventListener('ajaxProduct:added', this.onProductAdded.bind(this));
    if (!this.autoAddSuppressed) this.checkAndSyncInsurance();
  }

  onCartUpdate(event) {
    if (event?.source === 'shipping-insurance') return;
    if (event?.skipShippingInsuranceAutoAdd === true) return;

    // Quantity changes and deletions must still swap an existing insurance tier,
    // but should not re-add insurance after a deliberate removal.
    const skipAutoAdd = event?.source === 'cart-items';
    if (skipAutoAdd) {
      this.checkAndSyncInsurance(true);
    } else {
      setTimeout(() => this.checkAndSyncInsurance(false), 100);
    }
  }

  onProductAdded(event) {
    if (this.isInsuranceVariant(event.detail?.product?.id)) return;
    setTimeout(() => this.checkAndSyncInsurance(false), 100);
  }

  async checkAndSyncInsurance(skipAutoAdd = false) {
    if (this.isProcessing) return;

    try {
      this.isProcessing = true;
      const response = await fetch(`${theme.routes.cart_url}.js`);
      if (!response.ok) throw new Error('Failed to fetch cart');
      const cart = await response.json();
      const insuranceLines = cart.items.filter((item) => (
        this.isInsuranceVariant(item.variant_id)
      ));
      const merchandiseLines = cart.items.filter((item) => (
        !this.isInsuranceVariant(item.variant_id)
      ));
      const insuranceSuppressed = this.autoAddSuppressed || cart.items.some(
        (item) => item.properties?._pa_skip_shipping_insurance === 'true'
      );

      if (insuranceSuppressed) {
        if (insuranceLines.length) await this.removeInsurance(insuranceLines);
        return;
      }

      if (!merchandiseLines.length) {
        if (insuranceLines.length) await this.removeInsurance(insuranceLines);
        return;
      }

      const merchandiseSubtotal = this.getMerchandiseSubtotal(cart.items);
      const expectedTier = this.resolveTier(merchandiseSubtotal);
      if (!expectedTier?.variantId) return;

      // Owner decision Oct 13: premium ($1,000+) is opt-in. Keep a premium line the shopper added; otherwise never add
      // or upgrade into premium, and drop a lower tier that no longer fits.
      if (expectedTier.key === 'premium' || Number(expectedTier.minSubtotal || 0) >= 100000) {
        const hasOnlyPremium = insuranceLines.length === 1 &&
          this.normalizeId(insuranceLines[0].variant_id) === this.normalizeId(expectedTier.variantId);
        if (!hasOnlyPremium && insuranceLines.length) await this.removeInsurance(insuranceLines);
        if (!hasOnlyPremium || Number(insuranceLines[0].quantity) === 1) return;
      }

      const isAlreadyCorrect = (
        insuranceLines.length === 1 &&
        this.normalizeId(insuranceLines[0].variant_id) ===
          this.normalizeId(expectedTier.variantId) &&
        Number(insuranceLines[0].quantity) === 1
      );
      if (isAlreadyCorrect) return;
      if (!insuranceLines.length && skipAutoAdd) return;

      await this.syncInsuranceTier(expectedTier.variantId, insuranceLines);
    } catch (error) {
      console.error('Shipping Insurance: Error syncing cart', error);
    } finally {
      this.isProcessing = false;
    }
  }

  getSectionsToRender() {
    const sections = [];
    const miniCart = document.querySelector('mini-cart');
    if (miniCart?.getSectionsToRender) {
      sections.push(...miniCart.getSectionsToRender().map((section) => section.id));
    }

    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartItems?.dataset?.id) sections.push(cartItems.dataset.id);
    if (cartFooter?.dataset?.id) sections.push(cartFooter.dataset.id);
    return [...new Set(sections.filter(Boolean))];
  }

  renderCartPage(parsedState) {
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartItems?.dataset?.id && parsedState.sections?.[cartItems.dataset.id]) {
      const newContent = new DOMParser()
        .parseFromString(parsedState.sections[cartItems.dataset.id], 'text/html')
        .querySelector('.js-contents');
      const target = cartItems.querySelector('.js-contents');
      if (target && newContent) target.innerHTML = newContent.innerHTML;
    }

    if (cartFooter?.dataset?.id && parsedState.sections?.[cartFooter.dataset.id]) {
      const newContent = new DOMParser()
        .parseFromString(parsedState.sections[cartFooter.dataset.id], 'text/html')
        .querySelector('.js-contents');
      const target = cartFooter.querySelector('.js-contents');
      if (target && newContent) target.innerHTML = newContent.innerHTML;
    }

    if (parsedState.item_count === 0) {
      cartItems?.classList.add('is-empty');
      cartFooter?.classList.add('is-empty');
    }
  }

  updateCartBadges(parsedState) {
    ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
      const element = document.getElementById(id);
      if (!element || !parsedState.sections?.[id]) return;
      const newContent = new DOMParser()
        .parseFromString(parsedState.sections[id], 'text/html')
        .querySelector('.shopify-section');
      if (newContent) element.innerHTML = newContent.innerHTML;
    });
  }

  async updateInsurance(updates, attributeValue) {
    const sections = this.getSectionsToRender();
    const response = await fetch(`${theme.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        updates,
        attributes: { 'shipping-insurance': attributeValue },
        sections,
        sections_url: window.location.pathname
      })
    });
    if (!response.ok) throw new Error('Failed to update insurance');

    const parsedState = await response.json();
    this.core?.clearCartCache?.();
    this.updateCartBadges(parsedState);
    this.renderCartPage(parsedState);
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'shipping-insurance',
      cartData: parsedState
    });
  }

  async syncInsuranceTier(expectedVariantId, insuranceLines) {
    const updates = {};
    insuranceLines.forEach((line) => {
      updates[line.key || line.variant_id] = 0;
    });
    updates[this.normalizeId(expectedVariantId)] = 1;
    await this.updateInsurance(updates, 'true');
  }

  async removeInsurance(insuranceLines) {
    const updates = {};
    insuranceLines.forEach((line) => {
      updates[line.key || line.variant_id] = 0;
    });
    await this.updateInsurance(updates, '');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ShippingInsurance());
} else {
  new ShippingInsurance();
}
